import { createColumnHelper } from "@tanstack/react-table";
import { capitalCase } from "change-case";
import { useMemo, useState } from "react";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  formCategoryLabel,
  formTypeTheme,
  submissionExtendedLabel,
  submissionStatusLabel,
} from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Badge,
  CellWrapper,
  TableProps,
} from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { formatDateTime } from "@smart/itops-utils-basic";
import {
  mapToMatterSubmission,
  MatterSubmission,
  useLoadSetting,
} from "@smart/manage-gql-client-dom";

import {
  filterByDatePeriod,
  filterBySelection,
  filterByTypeOfMatter,
  getOnRowClick,
  getStaffName,
  sortByStaffName,
  sortSchema,
  typeOfMatterFilterAllOption,
  typeOfMatterFilterOptions,
} from "./column-helpers";
import { PendingSubmissionAction } from "./pending-submission-action";
import {
  filterByReminderStatus,
  getReminderFilterTags,
  ReminderCell,
  reminderStatusFilterOptions,
} from "./reminder-column";
import { useTimeFilterValue } from "./time-filter-value";
import { SubmissionStatusPill } from "../../../shared/submission-status-pill";
import {
  ExtendedGqlSubmission,
  GqlSubmission,
  GqlSubmissionSummary,
} from "../../types";

const toSubmission = (
  submissionSummary: GqlSubmissionSummary,
): GqlSubmission => ({
  ...submissionSummary,
  form: {
    ...submissionSummary.form,
    fields: [],
  },
  responses: [],
});

export const usePendingSubmissionsColumns = () => {
  const [cancelling, onCancel] = useState<MatterSubmission>();
  const [deleting, onDelete] = useState<MatterSubmission>();
  const [reminding, onRemind] = useState<MatterSubmission>();

  const { terms, dateDisplayFormat } = loadLocale();
  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "values.updatedAt", desc: true }],
    key: "pending-list-sort",
    schema: sortSchema,
    storage: "local",
  });
  const { staff, host } = useSmokeballApp();
  const { "LINK-3736": automaticRemindersFlag } = useFeatureFlags();
  const { result: setting, loading: reminderSettingLoading } = useLoadSetting(
    {},
    { skip: !automaticRemindersFlag },
  );

  const columns = useMemo(() => {
    const col = createColumnHelper<ExtendedGqlSubmission>();

    return [
      col.accessor("form.values.title", {
        header: "Form Name",
        meta: {
          showRowCount: true,
          filter: {
            type: "textInput",
            name: "formName",
            title: "Form Name",
            placeholder: "All",
            order: 1,
          },
        },
        cell: ({ getValue }) => (
          <CellWrapper label={getValue()} size="medium" />
        ),
      }),
      col.accessor("typeOfMatter", {
        id: "matter-category",
        header: "Type",
        meta: {
          filter: {
            type: "radio",
            name: "matterCategory",
            title: "Type (Lead/Matter)",
            order: 2,
            fixedOptions: typeOfMatterFilterOptions,
            defaultOption: typeOfMatterFilterAllOption,
          },
        },
        cell: ({ getValue }) => (
          <Badge
            label={getValue() ? formCategoryLabel[getValue()!] : undefined}
            theme={getValue() ? formTypeTheme[getValue()!] : undefined}
          />
        ),
        filterFn: filterByTypeOfMatter,
      }),
      col.accessor("values.recipient.contactName", {
        id: "recipient-name",
        header: "Contact",
        enableColumnFilter: false,
        cell: ({ getValue }) => <CellWrapper label={getValue()} />,
      }),
      col.accessor("matterTypeName", {
        header: "Matter Type",
        enableColumnFilter: false,
        cell: ({ getValue }) => <CellWrapper label={getValue()} />,
      }),
      col.accessor("matter.personResponsibleStaffId", {
        id: "personResponsibleStaffId",
        header: terms.responsiblePerson.header,
        meta: {
          filter: {
            type: "select",
            name: "personResponsible",
            title: capitalCase(terms.responsiblePerson.singular),
            order: 3,
            placeholder: "All",
            fixedOptions: [
              { value: "", label: "Unassigned" },
              ...(
                staff?.current?.map((s) => ({
                  value: s.id,
                  label: getStaffName({ staff: staff?.current, staffId: s.id }),
                })) || []
              ).sort((a, b) => a.label.localeCompare(b.label)),
            ],
          },
        },
        cell: ({ getValue }) => (
          <CellWrapper
            label={getStaffName({ staff: staff?.current, staffId: getValue() })}
            hideTooltip={!getValue()}
          />
        ),
        filterFn: filterBySelection,
        sortingFn: (rowA, rowB) =>
          sortByStaffName({
            staff: staff?.current,
            staffAId: rowA.original.matter?.personResponsibleStaffId,
            staffBId: rowB.original.matter?.personResponsibleStaffId,
          }),
      }),
      col.accessor("values.createdAt", {
        id: "shared-at",
        header: "Shared",
        meta: {
          filter: {
            type: "dateRange",
            name: "sharedAt",
            order: 4,
            title: "Date Shared",
            defaultOption: "allTime",
            unrestricted: true,
          },
        },
        cell: ({ getValue }) => (
          <CellWrapper label={formatDateTime(getValue(), dateDisplayFormat)} />
        ),
        filterFn: filterByDatePeriod,
      }),
      col.accessor("values.updatedAt", {
        header: "Modified On",
        id: "values.updatedAt",
        sortDescFirst: true,
        cell: ({ getValue }) => (
          <CellWrapper label={formatDateTime(getValue(), dateDisplayFormat)} />
        ),
        enableColumnFilter: false,
      }),
      ...(automaticRemindersFlag
        ? [
            col.accessor(
              (row) => getReminderFilterTags(row.values, setting?.reminder),
              {
                id: "reminders",
                header: "Reminder sent",
                enableSorting: false,
                meta: {
                  filter: {
                    type: "select",
                    name: "reminderStatus",
                    title: "Reminder sent",
                    order: 6,
                    placeholder: "All",
                    fixedOptions: reminderStatusFilterOptions,
                  },
                },
                cell: ({ row }) => (
                  <ReminderCell
                    submission={row.original.values}
                    setting={setting?.reminder}
                    loading={reminderSettingLoading}
                  />
                ),
                filterFn: filterByReminderStatus,
              },
            ),
          ]
        : []),
      col.accessor("derivedSubmissionStatus", {
        id: "status",
        header: "Status",
        meta: {
          filter: {
            title: "Activity Status",
            type: "select",
            name: "status",
            order: 5,
            placeholder: "All",
            fixedOptions: [
              { value: "draft", label: submissionStatusLabel.draft },
              {
                value: "prefilled",
                label: submissionExtendedLabel.internalUse,
              },
              { value: "active", label: submissionStatusLabel.active },
            ],
          },
        },
        cell: ({ row }) => (
          <SubmissionStatusPill
            syncStatus={row.original.values.syncStatus}
            status={row.original.values.status}
            isInternalUse={
              row.original.values.status === "draft" &&
              row.original.values.communicationMethod === "internalUse"
            }
          />
        ),
        filterFn: "arrIncludesSome",
      }),
      col.display({
        id: "actions",
        cell: PendingSubmissionAction({
          onCancel: (submission) =>
            onCancel(mapToMatterSubmission(toSubmission(submission))),
          onDelete: (submission) =>
            onDelete(mapToMatterSubmission(toSubmission(submission))),
          onRemind: (submission) =>
            onRemind(mapToMatterSubmission(toSubmission(submission))),
        }),
        meta: { action: true },
        size: 65,
      }),
    ];
  }, [
    staff?.current?.length,
    setting,
    reminderSettingLoading,
    automaticRemindersFlag,
  ]);
  const select: TableProps<GqlSubmission>["select"] = {
    state: {},
    onChange: (selected) => {
      const value = typeof selected === "function" ? selected({}) : selected;
      console.debug("selected row: ", value);
    },
  };

  const sort: TableProps<GqlSubmission>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  const { timeFilter, setTimeFilter } = useTimeFilterValue({
    filterStorageKey: "shared-at-date-filter",
  });

  return {
    columns,
    select,
    sort,
    timeFilter,
    setTimeFilter,
    onRowClick: getOnRowClick(host?.navigate),
    cancelling: { selected: cancelling, onClose: () => onCancel(undefined) },
    deleting: { selected: deleting, onClose: () => onDelete(undefined) },
    reminding: { selected: reminding, onClose: () => onRemind(undefined) },
  };
};
